import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { tokenStorage } from "./utils/tokenStorage";
import { client_id, user_pool_id, WS_URL, TEST_WS_URL, IS_PRODUCTION } from "./utils/url_constants";
import { CognitoRefreshToken, CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";

const WebSocketContext = createContext();

let wsInstance = null;
let reconnectTimeout = null;
let pingInterval = null;
let pongTimeout = null;

const poolData = {
    UserPoolId: user_pool_id,
    ClientId: client_id
};
const userPool = new CognitoUserPool(poolData);

// Environment Configuration
const url = IS_PRODUCTION ? WS_URL : TEST_WS_URL;

/**
 * Universal Token Refresh Handler
 * Routes to Cognito or Entra ID refresh mechanism dynamically.
 */
const refreshTokens = async () => {
    const authProvider = tokenStorage.getAuthProvider?.() || localStorage.getItem('authProvider') || 'cognito';

    if (authProvider === 'entra') {
        // Handle Entra ID refresh (e.g., via MSAL acquireTokenSilent or custom storage handler)
        if (typeof tokenStorage.refreshEntraToken === 'function') {
            return await tokenStorage.refreshEntraToken();
        }

        // Fallback if tokenStorage exposes a standard refresh or access token getter
        const refreshedToken = tokenStorage.getAccessToken?.() || tokenStorage.getIdToken?.();
        if (refreshedToken) {
            return { idToken: refreshedToken, accessToken: refreshedToken };
        }

        throw new Error('Entra ID token refresh mechanism not configured in tokenStorage.');
    }

    // AWS Cognito Refresh Logic
    return new Promise((resolve, reject) => {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) {
            return reject(new Error('No Cognito refresh token available'));
        }

        const cognitoUser = new CognitoUser({
            Username: localStorage.getItem('lastUsername') || '',
            Pool: userPool
        });

        const refreshCognitoToken = new CognitoRefreshToken({
            RefreshToken: refreshToken
        });

        cognitoUser.refreshSession(refreshCognitoToken, (err, session) => {
            if (err) {
                console.error('Cognito token refresh failed:', err);
                tokenStorage.clearTokens();
                return reject(err);
            }

            tokenStorage.setTokens(
                session.idToken.jwtToken,
                session.accessToken.jwtToken,
                session.refreshToken.token,
                session.idToken.payload.exp - Math.floor(Date.now() / 1000)
            );

            resolve({
                idToken: session.idToken.jwtToken,
                accessToken: session.accessToken.jwtToken
            });
        });
    });
};

/**
 * Retrieves the appropriate token for WebSocket query parameter based on active provider
 */
const getActiveAuthToken = () => {
    const authProvider = tokenStorage.getAuthProvider?.() || localStorage.getItem('authProvider') || 'cognito';

    if (authProvider === 'entra') {
        // Entra ID validation often expects the ID token or Access Token depending on backend setup
        return tokenStorage.getIdToken?.() || tokenStorage.getAccessToken?.();
    }

    return tokenStorage.getIdToken?.();
};

const PERMANENT_ERROR_CODES = [1008, 1003, 1007];

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
    const [ws, setWs] = useState(null);
    const [messages, setMessages] = useState([]);
    const [connectionError, setConnectionError] = useState(null);

    const reconnectAttemptsRef = useRef(0);
    const isReconnectingRef = useRef(false);
    const tokenRefreshAttemptedRef = useRef(false);
    const lastPongRef = useRef(Date.now());

    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [isReconnecting, setIsReconnecting] = useState(false);

    const clearPingPong = () => {
        if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
        }
        if (pongTimeout) {
            clearTimeout(pongTimeout);
            pongTimeout = null;
        }
    };

    const startHeartbeat = (webSocket) => {
        clearPingPong();

        pingInterval = setInterval(async () => {
            if (webSocket && webSocket.readyState === WebSocket.OPEN) {

                const is_expired = tokenStorage.isTokenExpired ? tokenStorage.isTokenExpired() : false;
                console.log('TOKEN EXPIRED:', is_expired ? 'YES' : 'NO');

                if (is_expired) {
                    console.log('TOKENS REFRESHING....');
                    try {
                        await refreshTokens();
                        console.log('Reconnecting WebSocket with fresh token...');
                        webSocket.close(4000, 'Token refreshed');
                    } catch (err) {
                        console.error('Token refresh during heartbeat failed:', err);
                        webSocket.close(4001, 'Token refresh failed');
                    }
                    return;
                }

                console.log('Sending ping...');
                const email = window.localStorage.getItem('email');
                webSocket.send(JSON.stringify({ type: 'ping', timestamp: Date.now(), email }));

                pongTimeout = setTimeout(() => {
                    console.log('Pong timeout — connection appears stale, reconnecting...');
                    webSocket.close(4000, 'Heartbeat timeout');
                }, 5000);
            }
        }, 30000);
    };

    const handlePong = () => {
        console.log('Received pong');
        lastPongRef.current = Date.now();

        if (pongTimeout) {
            clearTimeout(pongTimeout);
            pongTimeout = null;
        }

        tokenRefreshAttemptedRef.current = false;
    };

    const retryEmailFetch = (retries, delay) => {
        if (retries <= 0) {
            console.log('Retries exhausted. Unable to connect WebSocket.');
            setConnectionError('Unable to retrieve user email. Please try logging in again.');
            return;
        }

        setTimeout(() => {
            const email = window.localStorage.getItem('email');
            if (email) {
                console.log('Email found. Connecting WebSocket...');
                connectWebSocket();
            } else {
                console.log(`Retrying... Attempts left: ${retries - 1}`);
                retryEmailFetch(retries - 1, delay);
            }
        }, delay);
    };

    const handleReconnect = () => {
        if (reconnectTimeout || isReconnectingRef.current) return;

        if (reconnectAttemptsRef.current >= 5) {
            console.log('Maximum reconnection attempts reached. Giving up.');
            setConnectionError('Connection failed after multiple attempts. Please refresh the page.');
            isReconnectingRef.current = false;
            setIsReconnecting(false);
            return;
        }

        const attempts = reconnectAttemptsRef.current + 1;
        reconnectAttemptsRef.current = attempts;
        setReconnectAttempts(attempts);

        isReconnectingRef.current = true;
        setIsReconnecting(true);

        const delay = Math.min(attempts * 1000, 30000);
        console.log(`Reconnecting in ${delay / 1000} seconds... (Attempt ${attempts})`);

        reconnectTimeout = setTimeout(() => {
            reconnectTimeout = null;
            isReconnectingRef.current = false;
            setIsReconnecting(false);
            connectWebSocket();
        }, delay);
    };

    const connectWebSocket = async () => {
        setConnectionError(null);
        clearPingPong();

        const email = window.localStorage.getItem('email');

        if (!email) {
            console.log('No email address for WebSocket. Retrying in 5 seconds...');
            retryEmailFetch(3, 5000);
            return;
        }

        const is_expired = tokenStorage.isTokenExpired ? tokenStorage.isTokenExpired() : false;
        console.log('TOKEN EXPIRED:', is_expired ? 'YES' : 'NO');

        if (is_expired) {
            console.log('TOKENS REFRESHING....');
            try {
                await refreshTokens();
            } catch (err) {
                console.error('Token refresh before connect failed:', err);
                setConnectionError('Authentication failed. Please log in again.');
                return;
            }
        }

        const token = getActiveAuthToken();
        if (!token) {
            console.error('No auth token found for active session.');
            setConnectionError('Authentication token missing. Please log in again.');
            return;
        }

        const wsUrl = `${url}?email=${encodeURIComponent(email)}&auth=${encodeURIComponent(token)}`;
        const webSocket = new WebSocket(wsUrl);

        webSocket.onopen = () => {
            console.log('WebSocket connection established using ' + email);

            reconnectAttemptsRef.current = 0;
            setReconnectAttempts(0);
            lastPongRef.current = Date.now();

            startHeartbeat(webSocket);
        };

        webSocket.onmessage = (message) => {
            console.log('Received raw:', message);
            const data = JSON.parse(message.data);
            console.log('Received message:', data);

            if (data.type === 'server_shutdown') {
                console.log('Server is shutting down. Will attempt reconnect shortly...');
                if (data.reconnect) {
                    setTimeout(() => {
                        console.log('Reconnecting after server shutdown...');
                        reconnectAttemptsRef.current = 0;
                        setReconnectAttempts(0);
                        connectWebSocket();
                    }, 5000);
                }
                return;
            }

            if (data.type === 'pong') {
                handlePong();
                return;
            }

            tokenRefreshAttemptedRef.current = false;
            setMessages((prevMessages) => [...prevMessages, data]);
        };

        webSocket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        webSocket.onclose = async (event) => {
            console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`);

            clearPingPong();

            if (event.code === 1001 && event.reason === 'Server shutting down') {
                console.log('Server is shutting down gracefully. Reconnecting in 5s...');
                setTimeout(() => {
                    reconnectAttemptsRef.current = 0;
                    setReconnectAttempts(0);
                    connectWebSocket();
                }, 5000);
                return;
            }

            if (event.code === 1000) {
                tokenRefreshAttemptedRef.current = false;
                return;
            }

            // Permanent auth errors — try token refresh once, then give up
            if (PERMANENT_ERROR_CODES.includes(event.code) || event.reason === 'Invalid user') {
                console.log('Permanent WebSocket error detected.');

                if (!tokenRefreshAttemptedRef.current) {
                    console.log('Attempting to refresh tokens before giving up...');
                    tokenRefreshAttemptedRef.current = true;

                    try {
                        await refreshTokens();
                        console.log('Tokens refreshed successfully, reconnecting...');
                        reconnectAttemptsRef.current = 0;
                        setReconnectAttempts(0);
                        handleReconnect();
                    } catch (err) {
                        console.error('Token refresh failed:', err);
                        setConnectionError('Authentication failed. Please log in again.');
                    }
                } else {
                    console.log('Token refresh already attempted. Authentication failed permanently.');
                    setConnectionError('Authentication failed after token refresh. Please log in again.');
                }
                return;
            }

            if (!isReconnectingRef.current) {
                handleReconnect();
            }
        };

        wsInstance = webSocket;
        setWs(webSocket);
    };

    const manualReconnect = () => {
        if (wsInstance) {
            wsInstance.close();
            wsInstance = null;
        }

        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
        clearPingPong();

        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        tokenRefreshAttemptedRef.current = false;

        setReconnectAttempts(0);
        setConnectionError(null);
        setIsReconnecting(false);

        connectWebSocket();
    };

    useEffect(() => {
        const token = getActiveAuthToken();
        if (token) {
            connectWebSocket();
        } else {
            console.log('No valid auth token available at mount. Please log in.');
        }

        return () => {
            if (wsInstance) {
                wsInstance.close();
                wsInstance = null;
            }
            clearTimeout(reconnectTimeout);
            clearPingPong();
        };
    }, []);

    return (
        <WebSocketContext.Provider value={{
            ws,
            messages,
            connectWebSocket,
            connectionError,
            reconnect: manualReconnect,
            reconnectAttempts,
            isReconnecting,
            lastPong: lastPongRef.current
        }}>
            {connectionError && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    backgroundColor: '#fee2e2',
                    borderBottom: '1px solid #f87171',
                    color: '#991b1b',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontFamily: 'sans-serif',
                    fontSize: '14px',
                }}>
                    <span>⚠️ {connectionError}</span>
                    <button
                        onClick={manualReconnect}
                        style={{
                            marginLeft: '16px',
                            padding: '6px 14px',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                        }}
                    >
                        Reconnect
                    </button>
                </div>
            )}
            {children}
        </WebSocketContext.Provider>
    );
};